/**
 * AppPageHeader Component
 * Unified page header template that encapsulates:
 * - Page title and optional subtitle
 * - Optional tab navigation
 * - Consistent Fabric-style styling (no card, flat design)
 */

import {
  TabList,
  Tab,
  SelectTabEvent,
  SelectTabData,
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeft20Regular } from '@fluentui/react-icons';

const useHeaderStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    ...shorthands.padding('0', '0', '16px', '0'),
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  backButton: {
    minWidth: 'auto',
    color: tokens.colorNeutralForeground2,
    '&:hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('10px'),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  tabsContainer: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  // Separator line shown when there are no tabs (for visual consistency)
  separator: {
    marginTop: '8px',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  // Selected tab text color matches brand color
  tabSelected: {
    '& .fui-Tab__content': {
      color: `${tokens.colorBrandForeground1} !important`,
      fontWeight: '600',
    },
  },
});

export interface TabConfig {
  /** Unique value for the tab */
  value: string;
  /** Display label for the tab */
  label: string;
}

export interface AppPageHeaderProps {
  /** Page title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional tab configuration */
  tabs?: TabConfig[];
  /** Currently selected tab value */
  selectedTab?: string;
  /** Callback when tab changes */
  onTabChange?: (value: string) => void;
  /** Optional content to render in the upper right corner of the header */
  headerActions?: React.ReactNode;
  /** Callback when back button is clicked - shows back button when provided */
  onBack?: () => void;
  /** Test ID for the component */
  testId?: string;
}

export function AppPageHeader({
  title,
  subtitle,
  tabs,
  selectedTab,
  onTabChange,
  headerActions,
  onBack,
  testId,
}: AppPageHeaderProps) {
  const styles = useHeaderStyles();

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    if (onTabChange) {
      onTabChange(data.value as string);
    }
  };

  return (
    <div className={styles.container} data-testid={testId}>
      {/* Page Header Row - Title on left, actions on right */}
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          {onBack && (
            <Button
              appearance="subtle"
              icon={<ArrowLeft20Regular />}
              onClick={onBack}
              className={styles.backButton}
              aria-label="Go back"
            />
          )}
          <div className={styles.header}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && (
              <Text className={styles.subtitle}>{subtitle}</Text>
            )}
          </div>
        </div>
        {headerActions && (
          <div className={styles.headerActions}>{headerActions}</div>
        )}
      </div>

      {/* Tabs or Separator */}
      {tabs && tabs.length > 0 ? (
        <div className={styles.tabsContainer}>
          <TabList
            selectedValue={selectedTab}
            onTabSelect={handleTabSelect}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                className={selectedTab === tab.value ? styles.tabSelected : undefined}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>
        </div>
      ) : (
        <div className={styles.separator} />
      )}
    </div>
  );
}
